import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import {
  Card,
  Table,
  Alert,
  message,
  Select,
  Input,
  Button,
  Space,
  Tag,
  DatePicker,
  Row,
  Col,
  InputNumber,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { formatVnDateTimeShort, vnDayjs } from '../../utils/vnDateTime';
import {
  fetchWithdrawalRequests,
  type WithdrawalRequestRow,
  type WithdrawalStatus,
} from '../../services/merchantWithdrawalRequestsService';
import { EmptyState } from '../../design-system/EmptyState';
import { spacing } from '../../design-system/tokens';
import { formatCurrencyVND } from '../../utils/transactionHelpers';
import { withAdminPathPrefix } from '../../utils/adminPathPrefix';

const ROUNDED_CARD_STYLE: React.CSSProperties = {
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const PAGE_SIZE = 20;

const STATUS_OPTIONS: { value: WithdrawalStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'Tất cả trạng thái' },
  { value: 'PENDING', label: 'Chờ duyệt' },
  { value: 'APPROVED', label: 'Đã duyệt' },
  { value: 'PROCESSING', label: 'Đang chi hộ' },
  { value: 'REJECTED', label: 'Từ chối' },
  { value: 'COMPLETED', label: 'Hoàn tất CK' },
];

function statusTag(status: WithdrawalStatus) {
  const map: Record<WithdrawalStatus, { color: string; label: string }> = {
    PENDING: { color: 'gold', label: 'Chờ duyệt' },
    APPROVED: { color: 'blue', label: 'Đã duyệt' },
    PROCESSING: { color: 'processing', label: 'Đang chi hộ' },
    REJECTED: { color: 'red', label: 'Từ chối' },
    COMPLETED: { color: 'green', label: 'Hoàn tất' },
  };
  const m = map[status] || { color: 'default', label: status };
  return <Tag color={m.color}>{m.label}</Tag>;
}

export default function MerchantWithdrawalRequestsListPage() {
  const { pathname } = useLocation();
  const [, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<WithdrawalRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);

  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<WithdrawalStatus | 'ALL'>(
    'ALL'
  );
  const [restaurantId, setRestaurantId] = useState<number | undefined>(
    undefined
  );
  const defaultRange = (): [dayjs.Dayjs, dayjs.Dayjs] => {
    const end = dayjs();
    const start = end.subtract(29, 'day');
    return [start, end];
  };
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(() =>
    defaultRange()
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = range?.[0]?.format('YYYY-MM-DD');
      const to = range?.[1]?.format('YYYY-MM-DD');
      const res = await fetchWithdrawalRequests({
        page,
        limit: pageSize,
        q: q.trim() || undefined,
        keyword: q.trim() || undefined,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        restaurantId:
          restaurantId != null && !Number.isNaN(restaurantId)
            ? restaurantId
            : undefined,
        from,
        to,
        sortBy: 'created_at',
        sortOrder: 'desc',
      });
      if (res.success && res.data) {
        setRows(res.data);
        setTotal(res.pagination?.total ?? 0);
      } else {
        setRows([]);
        setTotal(0);
        setError(res.message || 'Không tải được danh sách');
      }
    } catch (e: unknown) {
      const ax = e as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      setError(ax.response?.data?.message || ax.message || 'Lỗi tải dữ liệu');
      message.error('Không tải được danh sách');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, q, statusFilter, restaurantId, range]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: ColumnsType<WithdrawalRequestRow> = [
    {
      title: 'Mã',
      dataIndex: 'public_code',
      key: 'public_code',
      width: 160,
      fixed: 'left',
      render: (code: string, record) => (
        <Link
          to={withAdminPathPrefix(
            pathname,
            `/withdrawal-requests/${record.id}`
          )}
        >
          {code}
        </Link>
      ),
    },
    {
      title: 'Quán',
      dataIndex: 'restaurant_name',
      key: 'restaurant_name',
      ellipsis: true,
      render: (name: string | null, r) => (
        <span>
          {name || '—'}{' '}
          <span style={{ color: '#888' }}>(#{r.restaurant_id})</span>
        </span>
      ),
    },
    {
      title: 'Số dư (snap)',
      dataIndex: 'balance_snapshot_vnd',
      key: 'balance_snapshot_vnd',
      width: 140,
      render: (v: number) => formatCurrencyVND(v),
    },
    {
      title: 'Yêu cầu rút',
      dataIndex: 'requested_amount_vnd',
      key: 'requested_amount_vnd',
      width: 130,
      render: (v: number) => formatCurrencyVND(v),
    },
    {
      title: 'Sau rút (ước tính)',
      dataIndex: 'estimated_balance_after_vnd',
      key: 'estimated_balance_after_vnd',
      width: 150,
      render: (v: number) => formatCurrencyVND(v),
    },
    {
      title: 'Ngân hàng',
      key: 'bank',
      ellipsis: true,
      render: (_, r) => (
        <span>
          {r.bank_name || '—'} · {r.bank_account_number || '—'}
        </span>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (s: WithdrawalStatus) => statusTag(s),
    },
    {
      title: 'Tạo lúc',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (v: string) => (v ? formatVnDateTimeShort(v) : '—'),
    },
  ];

  return (
    <div style={{ padding: spacing.lg }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
          Yêu cầu rút tiền
        </h1>
        <p style={{ marginTop: 8, color: '#666' }}>
          Kiểm tra số dư, TK ngân hàng và Transaction Logs trước khi duyệt.
          Duyệt sẽ tự động tạo lệnh chi hộ trên AppotaPay.
        </p>
      </div>

      <Card style={ROUNDED_CARD_STYLE}>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={10}>
            <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
              Tìm kiếm (mã, quán, STK,…)
            </div>
            <Input
              placeholder="Mã WR-, ID, tên quán, STK…"
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              value={q}
              onChange={e => setQ(e.target.value)}
              onPressEnter={() => {
                setPage(1);
                load();
              }}
              allowClear
              size="large"
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
              Trạng thái
            </div>
            <Select
              style={{ width: '100%' }}
              size="large"
              value={statusFilter}
              options={STATUS_OPTIONS}
              onChange={v => {
                setStatusFilter(v);
                setPage(1);
              }}
            />
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
              Restaurant ID
            </div>
            <InputNumber
              style={{ width: '100%' }}
              size="large"
              min={1}
              placeholder="ID quán"
              value={restaurantId}
              onChange={v =>
                setRestaurantId(v === null ? undefined : Number(v))
              }
            />
          </Col>
          <Col xs={24} lg={24}>
            <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
              Ngày tạo yêu cầu
            </div>
            <DatePicker.RangePicker
              value={range || undefined}
              onChange={dates =>
                setRange(
                  dates && dates[0] && dates[1] ? [dates[0], dates[1]] : null
                )
              }
              style={{ width: '100%', maxWidth: 360 }}
              format="DD/MM/YYYY"
            />
          </Col>
        </Row>
        <Space style={{ marginTop: 16 }}>
          <Button
            type="primary"
            size="large"
            onClick={() => {
              setPage(1);
              load();
            }}
            loading={loading}
          >
            Tìm kiếm
          </Button>
          <Button
            icon={<ReloadOutlined />}
            size="large"
            onClick={() => {
              setQ('');
              setStatusFilter('ALL');
              setRestaurantId(undefined);
              setRange(defaultRange());
              setPage(1);
              setSearchParams({}, { replace: true });
            }}
          >
            Đặt lại
          </Button>
        </Space>
      </Card>

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      <Card style={{ ...ROUNDED_CARD_STYLE, marginTop: spacing.lg }}>
        {!loading && rows.length === 0 ? (
          <EmptyState description="Không có yêu cầu" />
        ) : (
          <Table<WithdrawalRequestRow>
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={rows}
            scroll={{ x: 1100 }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps || PAGE_SIZE);
              },
            }}
          />
        )}
      </Card>
    </div>
  );
}
